import { css } from '@emotion/react';
import { formatDate } from '@frontend/date';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { ModalControlResponse, Text } from '@frontend/design-system';
import { InsuranceDetails } from '../utils/types';
import { DetailText } from './detail-text';
import { InsuranceStatusChip } from './insurance-status-chip';
import { InsuranceStatusChipButton } from './insurance-status-chip-button';

interface Props {
  insuranceDetails: InsuranceDetails;
  personId: string;
  locationId: string;
  showInProgressStatus: boolean;
  verifyInsuranceModalControl: ModalControlResponse;
}

export const InsuranceDetailList = ({
  insuranceDetails,
  personId,
  locationId,
  showInProgressStatus,
  verifyInsuranceModalControl,
}: Props) => {
  const { t } = useTranslation('insuranceDetails');

  return (
    <dl css={listStyle}>
      <div>
        <Text as='dt'>{t('NPI ID')}</Text>
        <DetailText value={insuranceDetails.nationalProviderId} required />
      </div>
      <div>
        <Text as='dt'>{t('Status')}</Text>
        {showInProgressStatus ? (
          <InsuranceStatusChip
            statusInfo={{
              text: t('Processing'),
              variant: 'warn',
              iconName: 'insurance-investigate',
            }}
          />
        ) : (
          <InsuranceStatusChipButton
            personId={personId}
            locationId={locationId}
            onClick={verifyInsuranceModalControl.openModal}
          />
        )}
      </div>
      <div>
        <Text as='dt'>{t('First Name')}</Text>
        <DetailText value={insuranceDetails.firstName} required />
      </div>
      <div>
        <Text as='dt'>{t('Last Name')}</Text>
        <DetailText value={insuranceDetails.lastName} required />
      </div>
      <div>
        <Text as='dt'>{t('Payer ID')}</Text>
        <DetailText value={insuranceDetails.payerId} required />
      </div>
      <div>
        <Text as='dt'>{t('Payer name')}</Text>
        <DetailText value={insuranceDetails.payerName} required />
      </div>
      <div>
        <Text as='dt'>{t('Group Number')}</Text>
        <DetailText value={insuranceDetails.groupNumber} />
      </div>
      <div>
        <Text as='dt'>{t('Effective Date')}</Text>
        <DetailText value={insuranceDetails.effectiveDate} />
      </div>
      <div>
        <Text as='dt'>{t('Member ID')}</Text>
        <DetailText value={insuranceDetails.memberId} required />
      </div>
      <div>
        <Text as='dt'>{t('Date of Birth')}</Text>
        <DetailText value={formatDate(insuranceDetails.dateOfBirth, 'MMMM D, YYYY')} required />
      </div>
    </dl>
  );
};

const listStyle = css`
  margin: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${theme.spacing(2)};
  grid-auto-flow: dense;
  > div {
    display: flex;
    flex-direction: column;

    dt {
      margin-right: ${theme.spacing(4)};
      font-size: ${theme.fontSize(12)};
      margin-bottom: ${theme.spacing(0.5)};
      color: ${theme.font.colors.subdued};
    }
  }
`;
